import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const HcservicesTab = () => {
  // Hcservices state
  const [hcservicesSettings, setHcservicesSettings] = useState([]);
  const [hcservicesLoading, setHcservicesLoading] = useState(false);
  const [showHcservicesSettingForm, setShowHcservicesSettingForm] = useState(false);
  const [newHcservicesSetting, setNewHcservicesSetting] = useState({
    keywords: '',
    startYear: new Date().getFullYear(),
    endYear: new Date().getFullYear() + 1,
    locations: []
  });
  const [hcservicesFormErrors, setHcservicesFormErrors] = useState({});
  const [hcservicesSubmitting, setHcservicesSubmitting] = useState(false);
  const [editingHcservicesSetting, setEditingHcservicesSetting] = useState(null);
  const [showHcservicesEditForm, setShowHcservicesEditForm] = useState(false);

  // Hcservices number settings state
  const [hcservicesNumberSettings, setHcservicesNumberSettings] = useState([]);
  const [hcservicesNumberLoading, setHcservicesNumberLoading] = useState(false);
  const [showHcservicesNumberForm, setShowHcservicesNumberForm] = useState(false);
  const [newHcservicesNumberSetting, setNewHcservicesNumberSetting] = useState({
    scraperType: 'hcservices',
    cnr_number: ''
  });
  const [hcservicesNumberErrors, setHcservicesNumberErrors] = useState({});
  const [hcservicesNumberSubmitting, setHcservicesNumberSubmitting] = useState(false);

  // Hcservices locations state
  const [hcservicesLocations, setHcservicesLocations] = useState([]);
  const [hcservicesLocationLoading, setHcservicesLocationLoading] = useState(false);
  const [hcservicesLocationSearchTerm, setHcservicesLocationSearchTerm] = useState('');
  const [hcservicesFilteredLocations, setHcservicesFilteredLocations] = useState([]);
  const [hcservicesLocationSearchTimeout, setHcservicesLocationSearchTimeout] = useState(null);

  // Hcservices scrape results state
  const [hcservicesScrapeResults, setHcservicesScrapeResults] = useState([]);
  const [hcservicesScrapeLoading, setHcservicesScrapeLoading] = useState(false);
  const [hcservicesSearchKeyword, setHcservicesSearchKeyword] = useState('');
  const [hcservicesSearchResults, setHcservicesSearchResults] = useState([]);
  const [hcservicesSearchLoading, setHcservicesSearchLoading] = useState(false);
  const [hcservicesPagination, setHcservicesPagination] = useState({
    total: 0,
    limit: 50,
    skip: 0,
    hasMore: false
  });

  // Hcservices number results state
  const [hcservicesNumberResults, setHcservicesNumberResults] = useState([]);
  const [hcservicesNumberResultsLoading, setHcservicesNumberResultsLoading] = useState(false);

  // Common state
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Fetch Hcservices locations when component mounts
  useEffect(() => {
    fetchHcservicesLocations();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hcservicesLocationSearchTimeout) {
        clearTimeout(hcservicesLocationSearchTimeout);
      }
    };
  }, [hcservicesLocationSearchTimeout]);

  // Load initial Hcservices scrape results
  useEffect(() => {
    searchHcservicesScrapeResults('', 0);
  }, []);

  // API function to fetch Hcservices number results
  const fetchHcservicesNumberResults = async () => {
    try {
      setHcservicesNumberResultsLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/number-data/hcservices-numbers');
      if (response.data.success) {
        // Ensure we have an array of results
        const results = response.data.data;
        if (Array.isArray(results)) {
          setHcservicesNumberResults(results);
        } else if (results && Array.isArray(results.results)) {
          setHcservicesNumberResults(results.results);
        } else {
          console.warn('Unexpected data structure for hcservices number results:', results);
          setHcservicesNumberResults([]);
        }
      } else {
        setHcservicesNumberResults([]);
      }
    } catch (error) {
      console.error('Error fetching hcservices number results:', error);
      setHcservicesNumberResults([]);
    } finally {
      setHcservicesNumberResultsLoading(false);
    }
  };

  // Load Hcservices number results when component mounts
  useEffect(() => {
    fetchHcservicesNumberResults();
  }, []);

  // Function to extract petitioner and respondent information
  const extractHcservicesPartyInfo = (petitionerVsRespondent) => {
    if (!petitionerVsRespondent) return { petitioners: '', respondents: '' };
    
    const parts = petitionerVsRespondent.split(' vs. ');
    if (parts.length === 2) {
      return {
        petitioners: parts[0].trim(),
        respondents: parts[1].trim()
      };
    }
    return { petitioners: petitionerVsRespondent, respondents: '' };
  };

  // Function to extract advocate information from documents
  const extractHcservicesAdvocateInfo = (documents) => {
    if (!documents || !documents['OTHER DOCUMENT(s)']) return { petitionerAdvocates: '', respondentAdvocates: '' };
    
    const docs = documents['OTHER DOCUMENT(s)'];
    const petitionerAdvocates = [];
    const respondentAdvocates = [];
    
    docs.forEach(doc => {
      if (doc['Document Type'] === 'VAKALATNAMA AND MEMO OF APPEARANCE') {
        const filedBy = doc['Filed By'];
        if (filedBy) {
          petitionerAdvocates.push(filedBy);
        }
      }
    });
    
    return {
      petitionerAdvocates: petitionerAdvocates.join(', '),
      respondentAdvocates: respondentAdvocates.join(', ')
    };
  };

  const fetchHcservicesLocations = async () => {
    try {
      setHcservicesLocationLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/hcservices/locations');
      if (response.data.success) {
        setHcservicesLocations(response.data.locations);
        setHcservicesFilteredLocations(response.data.locations);
      }
    } catch (error) {
      console.error('Error fetching hcservices locations:', error);
    } finally {
      setHcservicesLocationLoading(false);
    }
  };

  // API functions for Hcservices settings CRUD operations
  const addHcservicesSetting = async (settingData) => {
    try {
      const response = await api.post('http://45.32.210.228:3000/api/hcservices/settings/add', settingData);
      return response.data;
    } catch (error) {
      console.error('Error adding hcservices setting:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to add setting' };
    }
  };

  const updateHcservicesSetting = async (settingId, settingData) => {
    try {
      const response = await api.put(`http://45.32.210.228:3000/api/hcservices/settings/${settingId}`, settingData);
      return response.data;
    } catch (error) {
      console.error('Error updating hcservices setting:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to update setting' };
    }
  };

  const deleteHcservicesSetting = async (settingId) => {
    try {
      const response = await api.delete(`http://45.32.210.228:3000/api/hcservices/settings/${settingId}`);
      if (response.data.success) {
        console.log('Hcservices setting deleted successfully');
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Failed to delete setting' };
      }
    } catch (error) {
      console.error('Error deleting hcservices setting:', error);
      return { success: false, message: error.response?.data?.message || 'Error deleting hcservices setting' };
    }
  };

  const fetchHcservicesSettings = async () => {
    try {
      setHcservicesLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/hcservices/settings/load');
      if (response.data.success) {
        setHcservicesSettings(response.data.settings.settings || []);
      }
    } catch (error) {
      console.error('Error fetching hcservices settings:', error);
    } finally {
      setHcservicesLoading(false);
    }
  };

  const fetchHcservicesNumberSettings = async () => {
    try {
      setHcservicesNumberLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/number-settings?scraperType=hcservices');
      if (response.data.success) {
        setHcservicesNumberSettings(response.data.data.settings || []);
      }
    } catch (error) {
      console.error('Error fetching hcservices number settings:', error);
    } finally {
      setHcservicesNumberLoading(false);
    }
  };

  const addHcservicesNumberSetting = async (settingData) => {
    try {
      setHcservicesNumberSubmitting(true);
      const response = await api.post('http://45.32.210.228:3000/api/number-settings/add', settingData);
      if (response.data.success) {
        console.log('Hcservices number setting added successfully');
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Failed to add hcservices number setting' };
      }
    } catch (error) {
      console.error('Error adding hcservices number setting:', error);
      return { success: false, message: error.response?.data?.message || 'Error adding hcservices number setting' };
    } finally {
      setHcservicesNumberSubmitting(false);
    }
  };

  const deleteHcservicesNumberSetting = async (settingId) => {
    try {
      const response = await api.delete(`http://45.32.210.228:3000/api/number-settings/hcservices/id/${settingId}`);
      if (response.data.success) {
        console.log('Hcservices number setting deleted successfully');
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Failed to delete hcservices number setting' };
      }
    } catch (error) {
      console.error('Error deleting hcservices number setting:', error);
      return { success: false, message: error.response?.data?.message || 'Error deleting hcservices number setting' };
    }
  };

  const searchHcservicesScrapeResults = async (keyword, skip = 0) => {
    try {
      setHcservicesScrapeLoading(true);
      const response = await api.get(`http://45.32.210.228:3000/api/results/search?scraperType=hcservices&keywords=${keyword}&skip=${skip}&limit=50`);
      if (response.data.success) {
        if (skip === 0) {
          setHcservicesScrapeResults(response.data.data.results || []);
        } else {
          setHcservicesScrapeResults(prev => [...prev, ...(response.data.data.results || [])]);
        }
        setHcservicesPagination({
          total: response.data.data.total || 0,
          limit: 50,
          skip: skip + 50,
          hasMore: (response.data.data.results || []).length === 50
        });
      }
    } catch (error) {
      console.error('Error searching hcservices scrape results:', error);
    } finally {
      setHcservicesScrapeLoading(false);
    }
  };

  const handleHcservicesLocationSearch = (searchTerm) => {
    setHcservicesLocationSearchTerm(searchTerm);
    
    if (hcservicesLocationSearchTimeout) {
      clearTimeout(hcservicesLocationSearchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (searchTerm.trim() === '') {
        setHcservicesFilteredLocations(hcservicesLocations);
      } else {
        const filtered = hcservicesLocations.filter(location =>
          location.text.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setHcservicesFilteredLocations(filtered);
      }
    }, 300);
    
    setHcservicesLocationSearchTimeout(timeout);
  };

  const handleHcservicesLocationRemove = (index) => {
    setSelectedLocations(selectedLocations.filter((_, i) => i !== index));
  };

  const handleLocationSelect = (location) => {
    const isAlreadySelected = selectedLocations.some(selected => selected.value === location.value);
    if (!isAlreadySelected) {
      setSelectedLocations([...selectedLocations, location]);
    }
    setHcservicesLocationSearchTerm('');
    setShowLocationDropdown(false);
  };

  // Form handling functions
  const validateForm = (data) => {
    const errors = {};
    if (!data.keywords) {
      errors.keywords = 'Keywords is required';
    }
    if (!data.startYear || !data.endYear) {
      errors.yearRange = 'Start and end years are required';
    }
    if (data.startYear > data.endYear) {
      errors.yearRange = 'Start year cannot be greater than end year';
    }
    if (data.locations.length === 0) {
      errors.locations = 'At least one location is required';
    }
    return errors;
  };

  // Hcservices form handlers
  const handleAddHcservicesSetting = async (e) => {
    e.preventDefault();
    
    const settingData = {
      keywords: newHcservicesSetting.keywords,
      startYear: parseInt(newHcservicesSetting.startYear),
      endYear: parseInt(newHcservicesSetting.endYear),
      locations: selectedLocations.map(location => ({
        state_code: location.stateCode,
        court_code: location.courtCode
      }))
    };

    const errors = validateForm(settingData);
    if (Object.keys(errors).length > 0) {
      setHcservicesFormErrors(errors);
      return;
    }

    const result = await addHcservicesSetting(settingData);
    if (result.success) {
      // Reset form
      setNewHcservicesSetting({
        keywords: '',
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear() + 1,
        locations: []
      });
      setSelectedLocations([]);
      setHcservicesFormErrors({});
      setShowHcservicesSettingForm(false);
      
      // Refresh settings list
      fetchHcservicesSettings();
      
      // Show success message
      alert('Hcservices setting added successfully!');
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleEditHcservicesSetting = async (e) => {
    e.preventDefault();
    
    const settingData = {
      keywords: editingHcservicesSetting.keywords,
      startYear: parseInt(editingHcservicesSetting.startYear),
      endYear: parseInt(editingHcservicesSetting.endYear),
      locations: selectedLocations.map(location => ({
        state_code: location.stateCode,
        court_code: location.courtCode
      }))
    };

    const errors = validateForm(settingData);
    if (Object.keys(errors).length > 0) {
      setHcservicesFormErrors(errors);
      return;
    }

    const result = await updateHcservicesSetting(editingHcservicesSetting.setting_id, settingData);
    if (result.success) {
      // Reset form
      setEditingHcservicesSetting(null);
      setSelectedLocations([]);
      setHcservicesFormErrors({});
      setShowHcservicesEditForm(false);
      
      // Refresh settings list
      fetchHcservicesSettings();
      
      // Show success message
      alert('Hcservices setting updated successfully!');
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleDeleteHcservicesSetting = async (settingId) => {
    if (window.confirm('Are you sure you want to delete this hcservices setting?')) {
      const result = await deleteHcservicesSetting(settingId);
      if (result.success) {
        // Refresh settings list
        fetchHcservicesSettings();
        
        // Show success message
        alert('Hcservices setting deleted successfully!');
      } else {
        alert(`Error: ${result.message}`);
      }
    }
  };

  const openHcservicesEditForm = (setting) => {
    console.log('Opening hcservices edit form for setting:', setting);
    
    // Convert API locations format to component format
    const convertedLocations = setting.locations ? setting.locations.map(loc => {
      const matchingLocation = hcservicesLocations.find(l => 
        l.stateCode === loc.state_code && l.courtCode === loc.court_code
      );
      
      if (matchingLocation) {
        return matchingLocation;
      } else {
        return {
          value: `${loc.state_code}/${loc.court_code}`,
          text: `State: ${loc.state_code}, Court: ${loc.court_code}`,
          stateCode: loc.state_code,
          courtCode: loc.court_code
        };
      }
    }) : [];
    
    setEditingHcservicesSetting(setting);
    setSelectedLocations(convertedLocations);
    setShowHcservicesEditForm(true);
  };

  const handleAddHcservicesNumberSetting = async (e) => {
    e.preventDefault();
    
    if (!newHcservicesNumberSetting.cnr_number.trim()) {
      setHcservicesNumberErrors({ cnr_number: 'CNR Number is required' });
      return;
    }

    const result = await addHcservicesNumberSetting(newHcservicesNumberSetting);
    if (result.success) {
      // Reset form
      setNewHcservicesNumberSetting({
        scraperType: 'hcservices',
        cnr_number: ''
      });
      setHcservicesNumberErrors({});
      setShowHcservicesNumberForm(false);
      
      // Refresh number settings list
      fetchHcservicesNumberSettings();
      
      // Show success message
      alert('Hcservices number setting added successfully!');
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleDeleteHcservicesNumberSetting = async (settingId) => {
    if (window.confirm('Are you sure you want to delete this hcservices number setting?')) {
      const result = await deleteHcservicesNumberSetting(settingId);
      if (result.success) {
        // Refresh hcservices number settings list
        fetchHcservicesNumberSettings();
        
        // Show success message
        alert('Hcservices number setting deleted successfully!');
      } else {
        alert(`Error: ${result.message}`);
      }
    }
  };

  // Add CNR number from scrape result to hcservices number settings
  const handleHcservicesAddToDetail = async (cnrNumber) => {
    if (!cnrNumber) {
      alert('No CNR number available for this result');
      return;
    }

    // Check if CNR number already exists
    const existingSetting = hcservicesNumberSettings.find(setting => setting.cnr_number === cnrNumber);
    if (existingSetting) {
      alert('This CNR number is already in your hcservices number settings');
      return;
    }

    // Add the CNR number to hcservices number settings
    const settingData = {
      scraperType: 'hcservices',
      cnr_number: cnrNumber
    };

    const result = await addHcservicesNumberSetting(settingData);
    if (result.success) {
      // Refresh hcservices number settings list
      fetchHcservicesNumberSettings();
      
      // Show success message
      alert(`CNR number ${cnrNumber} added to hcservices number settings successfully!`);
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleHcservicesSearch = (e) => {
    e.preventDefault();
    searchHcservicesScrapeResults(hcservicesSearchKeyword, 0);
  };

  const handleHcservicesLoadMore = () => {
    searchHcservicesScrapeResults(hcservicesSearchKeyword, hcservicesPagination.skip);
  };

  // Load data when component mounts
  useEffect(() => {
    fetchHcservicesSettings();
    fetchHcservicesNumberSettings();
  }, []);

  const renderKeywordTab = () => (
    <div className="space-y-6">
      {/* Settings List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Hcservices Settings</h3>
            <button
              onClick={() => setShowHcservicesSettingForm(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Setting
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {hcservicesLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading hcservices settings...</p>
            </div>
          ) : hcservicesSettings.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hcservices settings found</p>
          ) : (
            <div className="space-y-4">
              {hcservicesSettings.map((setting) => (
                <div key={setting.setting_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Keywords: {setting.keywords}</h4>
                      <p className="text-sm text-gray-600">Year Range: {setting.startYear} - {setting.endYear}</p>
                      <p className="text-sm text-gray-600">Locations: {setting.locations?.length || 0} selected</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openHcservicesEditForm(setting)}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteHcservicesSetting(setting.setting_id)}
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scrape Results */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Hcservices Scrape Results</h3>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleHcservicesSearch} className="mb-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={hcservicesSearchKeyword}
                onChange={(e) => setHcservicesSearchKeyword(e.target.value)}
                placeholder="Search by keyword..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={hcservicesScrapeLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {hcservicesScrapeLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>

          {hcservicesScrapeLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Searching...</p>
            </div>
          ) : hcservicesScrapeResults.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No results found</p>
          ) : (
            <div className="space-y-4">
              {hcservicesScrapeResults.map((result) => (
                <div key={result._id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900">Case Number: {result.case_no || '-'}</h4>
                  <p className="text-sm text-gray-600">CNR: {result.cnr_number || '-'}</p>
                  <p className="text-sm text-gray-600">PET Name: {result.pet_name || '-'}</p>
                  <p className="text-sm text-gray-600">RESPONDENT Name: {result.res_name || '-'}</p>
                  <button
                    onClick={() => handleHcservicesAddToDetail(result.cnr_number)}
                    className="mt-2 px-3 py-1 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Add to Detail
                  </button>
                </div>
              ))}
              
              {hcservicesPagination.hasMore && (
                <div className="text-center pt-4">
                  <button
                    onClick={handleHcservicesLoadMore}
                    disabled={hcservicesScrapeLoading}
                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderNumberTab = () => (
    <div className="space-y-6">
      {/* Number Settings */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Hcservices CNR Number Settings</h3>
            <button
              onClick={() => setShowHcservicesNumberForm(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add CNR Number
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {hcservicesNumberLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading hcservices number settings...</p>
            </div>
          ) : hcservicesNumberSettings.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hcservices number settings found</p>
          ) : (
            <div className="space-y-4">
              {hcservicesNumberSettings.map((setting) => (
                <div key={setting._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium text-gray-900">CNR Number: {setting.cnr_number}</h4>
                    </div>
                    <button
                      onClick={() => handleDeleteHcservicesNumberSetting(setting._id)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Number Results Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Hcservices Number Results</h3>
        </div>
        
        <div className="p-6">
          {hcservicesNumberResultsLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading hcservices number results...</p>
            </div>
          ) : (Array.isArray(hcservicesNumberResults) ? hcservicesNumberResults : []).length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hcservices number results found</p>
          ) : (
            <div className="space-y-4">
              {(Array.isArray(hcservicesNumberResults) ? hcservicesNumberResults : []).map((result, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900">CNR Number: {result.cnr_number || '-'}</h4>
                  <p className="text-sm text-gray-600">Status/Stage: {result.caseStatus?.casestage || result.caseStatus?.casestatus || '-'}</p>
                  <p className="text-sm text-gray-600">Petitioner Advocate(s): {Array.isArray(result.petitionerAdvocate) ? result.petitionerAdvocate.join(', ') : result.petitionerAdvocate || '-'}</p>
                  <p className="text-sm text-gray-600">Respondent Advocate(s): {Array.isArray(result.respondentAdvocate) ? result.respondentAdvocate.join(', ') : result.respondentAdvocate || '-'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return {
    renderKeywordTab,
    renderNumberTab,
    showHcservicesSettingForm,
    setShowHcservicesSettingForm,
    showHcservicesEditForm,
    setShowHcservicesEditForm,
    showHcservicesNumberForm,
    setShowHcservicesNumberForm,
    newHcservicesSetting,
    setNewHcservicesSetting,
    editingHcservicesSetting,
    setEditingHcservicesSetting,
    newHcservicesNumberSetting,
    setNewHcservicesNumberSetting,
    hcservicesFormErrors,
    setHcservicesFormErrors,
    hcservicesNumberErrors,
    setHcservicesNumberErrors,
    hcservicesSubmitting,
    setHcservicesSubmitting,
    hcservicesNumberSubmitting,
    setHcservicesNumberSubmitting,
    hcservicesLocations,
    selectedLocations,
    setSelectedLocations,
    hcservicesLocationSearchTerm,
    setHcservicesLocationSearchTerm,
    hcservicesFilteredLocations,
    setHcservicesFilteredLocations,
    showLocationDropdown,
    setShowLocationDropdown,
    handleAddHcservicesSetting,
    handleEditHcservicesSetting,
    handleAddHcservicesNumberSetting,
    handleLocationSelect,
    handleHcservicesLocationRemove,
    handleHcservicesLocationSearch,
    handleHcservicesAddToDetail,
    fetchHcservicesNumberResults
  };
};

export default HcservicesTab; 